import { useState, useEffect, useCallback, createContext, useContext } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Branch Context for sharing across components
const BranchContext = createContext(null);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    // Return safe defaults when not wrapped in provider
    return {
      branches: [],
      currentBranch: null,
      loading: false,
      switchBranch: () => {},
      getHeaders: () => ({
        'Content-Type': 'application/json',
        'X-Merchant-ID': localStorage.getItem('backoffice_active_restaurant') || ''
      }),
      refreshBranches: () => {},
      hasMutipleBranches: false
    };
  }
  return context;
};

export function BranchProvider({ children }) {
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasMutipleBranches = branches.length > 1;

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Merchant-ID': localStorage.getItem('backoffice_active_restaurant') || '',
    ...(currentBranch?.id ? { 'X-Branch-Id': currentBranch.id.toString() } : {})
  }), [currentBranch]);

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem('backoffice_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const res = await fetch(`${API}/api/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Merchant-ID': localStorage.getItem('backoffice_active_restaurant') || ''
        }
      });
      
      if (!res.ok) {
        if (res.status === 401) {
          // Token invalid, don't set branches
          setLoading(false);
          return;
        }
        throw new Error('Failed to fetch branches');
      }
      
      const data = await res.json();
      setBranches(data.branches || []);
      
      // Set current branch from localStorage or first branch
      const savedBranchId = localStorage.getItem('current_branch_id');
      const savedBranch = savedBranchId 
        ? data.branches.find(b => b.id.toString() === savedBranchId)
        : null;
      
      if (savedBranch) {
        setCurrentBranch(savedBranch);
      } else if (data.branches.length > 0) {
        setCurrentBranch(data.branches[0]);
        localStorage.setItem('current_branch_id', data.branches[0].id.toString());
      }
      
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for auth changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'backoffice_token') {
        if (e.newValue) {
          fetchBranches();
        } else {
          setBranches([]);
          setCurrentBranch(null);
        }
      }
    };

    // Also listen for custom login event
    const handleLoginEvent = () => {
      fetchBranches();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('backoffice-login', handleLoginEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('backoffice-login', handleLoginEvent);
    };
  }, [fetchBranches]);


  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Switch branch
  const switchBranch = useCallback(async (branch) => {
    setCurrentBranch(branch);
    localStorage.setItem('current_branch_id', branch.id.toString());
    
    // Optionally persist to backend
    try {
      const token = localStorage.getItem('backoffice_token');
      await fetch(`${API}/api/branches/me/default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Merchant-ID': localStorage.getItem('backoffice_active_restaurant') || ''
        },
        body: JSON.stringify({ branchId: branch.id })
      });
    } catch (err) {
      console.error('Error saving default branch:', err);
    }
    
    // Trigger data reload across components
    window.dispatchEvent(new CustomEvent('branch-changed', { detail: branch }));
  }, []);

  const value = {
    branches,
    currentBranch,
    loading,
    switchBranch,
    getHeaders,
    refreshBranches: fetchBranches,
    hasMutipleBranches
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}

// Export a simple hook to get branch-aware headers
export function useBranchHeaders() {
  const { currentBranch } = useBranch();
  
  return useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Merchant-ID': localStorage.getItem('backoffice_active_restaurant') || '',
    ...(currentBranch?.id ? { 'X-Branch-Id': currentBranch.id.toString() } : {})
  }), [currentBranch]);
}

export default BranchContext;
